import { useState, lazy, Suspense, useRef, useMemo } from "react";
import Icon from "@mdi/react";
import {
    mdiCalendar, mdiChevronLeft, mdiChevronRight, mdiFile, mdiFloppy, mdiPen, mdiPlus, mdiStar, mdiStarOutline,
} from "@mdi/js";
import { Link, useRouter, useTitle } from "easy-page-router/react";
// import Lightbox from "fslightbox-react";
import Lightbox from "react-spring-lightbox";

import { useHeaders, useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { ContentSplit, FileIcon, Header, Loading, Remove, Separator } from "../components";
import { api } from "../services/api";

import { DownloadItem, Gallery, Project } from "../../types";
import { handleContentFiles } from "../services/editor";
import { resizeImage } from "easy-image-resizer";
import moment from "moment";
import { findIndex, uniq } from "lodash";
import Footer from "../components/Footer";
import { htmlToText } from "html-to-text";
import { DownloadAdmin, DownloadList, getFileName } from "../components/Download";


const Editor = lazy(() => import("../components/Editor"));

export default function ProjectPage({ url }: { url: string }) {
    const { push } = useRouter();
    const user = useUser();

    const [project, error, isLoading, reload] = useSSRHook<Project>(`/api/project?url=${encodeURIComponent(url)}`);

    const title = (project?.title ?? "Projekt") + " | TSO Krkonoše";
    const description = useMemo(() => {
        const description = htmlToText(project?.content ?? "", { limits: { maxInputLength: 2000 } }).slice(0, 150);
        if (!description) return "Stránka technického svazu obcí Krkonoše";
        return description;
    }, [project]);

    useTitle(title);
    useHeaders({
        title,
        description,
        image: window.location.origin + "/logo.png",
        canonical: window.location.origin + window.location.pathname,
    });


    const [projects] = useSSRHook<Project[]>("/api/projects");

    const years = useMemo(() => {
        if (!projects) return [moment().format("YYYY")];
        return uniq(projects.map(p => moment(p.date).format("YYYY"))).sort().reverse();
    }, [projects]);

    const [lightbox, setLightbox] = useState<null | number>(null);

    const [edit, setEdit] = useState<null | Project>(null);
    const [message, setMessage] = useState("");

    const refEditor = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const h1 = project?.title ?? "TSO Krkonoše";
    const projectYear = moment(project?.date).format("YYYY");

    const projectIndex = findIndex(projects || [], p => p._id === project?._id);
    const projectBefore = projects?.[projectIndex - 1] || null;
    const projectAfter = projects?.[projectIndex + 1] || null;

    async function handleSave() {
        if (!project) return;
        if (!edit) return;
        if (!refEditor.current) {
            setMessage("Nastal problém s editorem, zkuste to prosím později");
            return;
        }

        const { content, files } = await handleContentFiles(edit, refEditor.current);

        const [respond] = await api.updateProject({
            ...project,
            title: edit.title,
            date: edit.date,
            content,
            files,
        });

        if (!respond) {
            setMessage("Ukládání změn selhalo");
            return;
        }

        reload();
        setMessage("Projekt úspěšně uložen");
        setEdit(null);
        push(`/projekt/${respond.url}`);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function handleAddImages(e: any) {
        if (!project) return;

        setMessage("Přidávám obrázky do galerie...");
        const files = e.target.files as File[];
        const gallery: Gallery[] = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file) continue;

            gallery.push({
                full: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        maxWidth: 1920,
                        maxHeight: 1920,
                        type: "webp",
                    })
                },
                thumb: {
                    type: "EASY_DB_FILE", url: await resizeImage(file, {
                        // width: 235 * 2,
                        maxHeight: 169 * 2,
                        type: "webp",
                    })
                },
            });
        }

        if (gallery.length === 0) {
            setMessage("")
            return;
        }

        const [, error] = await api.updateProject({
            ...project,
            gallery: [
                ...project.gallery,
                ...gallery,
            ],
        })

        if (error) {
            setMessage("Ukládání fotografií selhalo");
            return;
        }

        reload();
        setMessage("Fotografie jsou přidány do projektu");
    }

    async function handleRemove() {
        if (!project) return;
        await api.removeProject({ projectId: project._id });
        push("/projekty");
    }

    async function handleRemoveImage(index: number) {
        if (!project) return;

        const [, error] = await api.updateProject({
            ...project,
            gallery: project.gallery.filter((_, i) => i !== index),
        });
        if (error) {
            setMessage("Ukládání změn selhalo");
            return;
        }
        setMessage("Obrázek v galerii vymazán");
        reload();
    }

    async function handleChangeDownloads(downloads: DownloadItem[]) {
        if (!project) return false;

        const [, error] = await api.updateProject({
            ...project,
            downloads,
        });

        reload();
        return error === null;
    }

    return (
        <>
            <main>
                <Header background="pens" title={h1}>
                    <Link to="/projekty">Projekty</Link>
                    <span className="space">-</span>
                    {h1}
                </Header>
                <ContentSplit
                    left={<>
                        {isLoading && <Loading />}
                        {error && (
                            <p className="alert">
                                Projekt se bohužel nepodařilo načíst 🙁
                            </p>
                        )}

                        {project && (
                            <>
                                {user.isAdmin && (
                                    <div className="admin">
                                        {!edit && <button onClick={() => setEdit(project)}>
                                            <Icon path={mdiPen} size={1} />{" "}
                                            Upravit stránku
                                        </button>}
                                        {!edit && <Remove onRemove={handleRemove}>Smazat projekt</Remove>}
                                        {edit && <button onClick={handleSave}>
                                            <Icon path={mdiFloppy} size={1} />{" "}
                                            Uložit změny
                                        </button>}
                                    </div>
                                )}

                                <Lightbox
                                    singleClickToZoom
                                    isOpen={lightbox !== null}
                                    currentIndex={lightbox || 0}
                                    images={project.gallery.map((f, i) => ({
                                        src: f.full.url,
                                        alt: `${h1} galerie ${i}`
                                    }))}
                                    onNext={() => {
                                        let next = (lightbox || 0) + 1;
                                        if (next >= project.gallery.length) next = project.gallery.length - 1;
                                        setLightbox(next);
                                    }}
                                    onPrev={() => {
                                        let prev = (lightbox || 0) - 1;
                                        if (prev < 0) prev = 0;
                                        setLightbox(prev);
                                    }}
                                    onClose={() => setLightbox(null)}
                                    style={{ background: "rgba(0, 0, 0, 0.5)", zIndex: 1000 }}
                                    renderHeader={() => <div className="gallery-header"><h1>{h1}</h1></div>}
                                    renderFooter={() => <div className="gallery thumbs">
                                        {project.gallery.map((f, i) => <img
                                            key={i}
                                            src={f.thumb.url}
                                            onClick={() => setLightbox(i)}
                                        />)}
                                    </div>}
                                />

                                {!edit && <>
                                    <h1>{project.title}</h1>
                                    <Separator />
                                    <div className="title-date">
                                        <Icon path={mdiCalendar} size={1} />{" "}
                                        {moment(project.date).format("LL")}
                                    </div>
                                    {user.isAdmin && message && <p className="alert">{message}</p>}
                                    <div className="html-content ck-content" dangerouslySetInnerHTML={{
                                        __html: project.content || "<p>Tento projekt nemá žádný text</p>",
                                    }} />

                                    <DownloadList downloads={project.downloads || []} />

                                    <div className="gallery">
                                        {project.gallery.map((f, i) => <img
                                            key={i}
                                            src={f.thumb.url}
                                            onClick={() => setLightbox(i)}
                                        />)}
                                    </div>
                                    <div className="project-arrows">
                                        <Link
                                            className={projectBefore ? "left" : "left disabled"}
                                            to={`/projekt/${encodeURIComponent(projectBefore?.url || "")}`}
                                        >
                                            <Icon path={mdiChevronLeft} size={2} />
                                            <span>{projectBefore?.title}</span>
                                        </Link>
                                        <Link
                                            className={projectAfter ? "right" : "right disabled"}
                                            to={`/projekt/${encodeURIComponent(projectAfter?.url || "")}`}
                                        >
                                            <span>{projectAfter?.title}</span>
                                            <Icon path={mdiChevronRight} size={2} />
                                        </Link>
                                    </div>
                                </>}
                                {user.isAdmin && edit && <>
                                    <input
                                        className="h1"
                                        type="text"
                                        value={edit.title}
                                        onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                                    />
                                    <Separator />
                                    {message && <p className="alert">{message}</p>}
                                    <input
                                        type="date"
                                        value={edit.date}
                                        onChange={e =>
                                            setEdit({ ...edit, date: moment(e.target.value).format("YYYY-MM-DD") })}
                                    />
                                    <div ref={refEditor}>
                                        <Suspense fallback={<Loading />}>
                                            <Editor
                                                value={edit.content}
                                                onChange={(value) => setEdit({ ...edit, content: value })}
                                            />
                                        </Suspense>
                                    </div>

                                    <DownloadAdmin downloads={project.downloads || []} onChange={handleChangeDownloads} />

                                    <div style={{ position: "relative", height: 100 }}>
                                        <div className="admin">
                                            <input
                                                style={{ display: "none" }}
                                                type="file"
                                                ref={inputRef}
                                                accept="image/jpeg,image/png,image/webp"
                                                multiple
                                                onChange={handleAddImages}
                                            />
                                            <button onClick={() => inputRef.current?.click()}>
                                                <Icon path={mdiPlus} size={1} /> Přidat fotografie
                                            </button>
                                        </div>
                                    </div>
                                    <div className="gallery">
                                        {project.gallery.map((f, i) => <div key={i}>
                                            <img
                                                src={f.thumb.url}
                                                onClick={() => setLightbox(i)}
                                            />
                                            <Remove onRemove={() => handleRemoveImage(i)} />
                                        </div>)}
                                    </div>
                                </>}
                            </>
                        )}
                    </>}
                    right={<>
                        {isLoading && <Loading />}
                        {projects && <ul className="cat-list">
                            {years.map(y => <li key={y} className={y === projectYear ? "current" : ""}>
                                <Link to={`/projekty?rok=${y}`}>
                                    <Icon path={y === projectYear ? mdiStar : mdiStarOutline} size={0.8} />
                                    {y}
                                </Link>
                            </li>)}
                        </ul>}
                    </>}
                />
            </main>
            <Footer />
        </>
    );
}